/**
 * PageLoader Component
 *
 * Full-screen loading overlay that blocks user interaction during page transitions.
 * Use this when navigating between pages or during critical loading states.
 *
 * Features:
 * - Full-screen overlay with gradient background
 * - Blocks all user interaction
 * - Centered spinner with optional message
 * - High z-index to appear above all content
 *
 * @example
 * ```tsx
 * import { PageLoader } from '@/components/ui/Loading';
 *
 * function MyPage() {
 *   const [isLoading, setIsLoading] = useState(true);
 *
 *   if (isLoading) {
 *     return <PageLoader message="Loading dashboard..." />;
 *   }
 *
 *   return <div>Page content</div>;
 * }
 * ```
 */

import { cn } from '@/lib/utils'

interface PageLoaderProps {
  /** Optional message to display below the spinner */
  message?: string
  /** Custom spinner size (default: 64px) */
  size?: number
  /** Custom className for additional styling */
  className?: string
}

export function PageLoader({ message, size = 64, className }: PageLoaderProps) {
  return (
    <div
      className={cn(
        'fixed inset-0 z-[9999] flex items-center justify-center',
        'bg-black/60 backdrop-blur-sm',
        className
      )}
    >
      <div className="flex flex-col items-center gap-4">
        {/* Spinner - uses primary color */}
        <div
          className="border-4 border-primary border-t-transparent rounded-full animate-spin"
          style={{
            width: `${size}px`,
            height: `${size}px`,
          }}
        />

        {/* Optional message */}
        {message && (
          <p className="text-white text-lg font-medium drop-shadow-lg text-center px-4">
            {message}
          </p>
        )}
      </div>
    </div>
  )
}

export default PageLoader
